import { useEffect, useRef, useState, type ReactNode } from 'react'

/** Keep one permission-filtered menu. The native modal supplies focus containment,
 * Escape and focus restoration on phones without duplicating navigation rules. */
export function TenantNavigation({ children, tenantName, context }: { children: ReactNode; tenantName: string; context: string }) {
  const [mobile, setMobile] = useState(() => window.matchMedia?.('(max-width: 720px)').matches ?? false)
  const dialog = useRef<HTMLDialogElement>(null)
  const [open, setOpen] = useState(false)
  // close() on a closed dialog is a no-op per spec, but showModal() on an open one
  // throws; guard both so neither depends on the caller knowing the state.
  const shut = () => { if (dialog.current?.open) dialog.current.close() }
  const close = () => { shut(); setOpen(false) }

  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 720px)')
    if (!media) return
    const change = () => { shut(); setOpen(false); setMobile(media.matches) }
    media.addEventListener('change', change)
    return () => media.removeEventListener('change', change)
  }, [])

  useEffect(() => { shut() }, [context])

  if (!mobile) return <aside className="tenant-sidebar">{children}</aside>
  return <>
    <header className="tenant-mobile-header">
      <img src="/parkify-logo-color.svg" alt="Parkify" />
      <span title={tenantName}>{tenantName}</span>
      <button type="button" aria-haspopup="dialog" aria-controls="tenant-mobile-menu" aria-expanded={open}
        onClick={() => { if (!dialog.current?.open) dialog.current?.showModal(); setOpen(true) }}>Menu</button>
    </header>
    <dialog ref={dialog} id="tenant-mobile-menu" className="tenant-mobile-menu" aria-label="Tenant navigation"
      onClose={() => setOpen(false)}
      onClick={e => { if (e.target === e.currentTarget) close() }}>
      <div className="tenant-menu-content" onClickCapture={e => {
        // Close before the destination opens its own modal (for example Access).
        if ((e.target as HTMLElement).closest('button')) close()
      }}>
        <button type="button" className="tenant-menu-close" onClick={close} autoFocus>Close menu</button>
        {children}
      </div>
    </dialog>
  </>
}
